import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Shield, ArrowRight, ArrowLeft, Plus, X, CheckCircle } from 'lucide-react';

const SCORE_RANGES = ['300-500', '500-580', '580-620', '620-680', '680+'];
const ITEM_TYPES = ['collection', 'late_payment', 'charge_off', 'bankruptcy', 'foreclosure', 'repossession', 'lien', 'judgment'];
const BUREAUS = ['Experian', 'Equifax', 'TransUnion'];
const TIERS = [
    { id: 'foundation', name: 'Foundation', price: 'FREE', desc: 'Basic credit repair with up to 5 disputes per month' },
    { id: 'accelerated', name: 'Accelerated', price: 'FREE', desc: 'AI dispute letters, score simulator, breach scanner, bi-weekly check-ins' },
    { id: 'premium', name: 'Premium', price: 'FREE', desc: 'Unlimited disputes, priority processing, dedicated advisor' },
];

export default function CreditRepairIntakePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        full_name: user?.name || '',
        phone: '',
        current_score_range: '500-580',
        target_score: 640,
        monthly_budget: 0,
        program_tier: 'accelerated',
        negative_items: [],
    });
    const [newItem, setNewItem] = useState({ bureau: 'Experian', item_type: 'collection', creditor_name: '', account_number: '', amount: '' });

    const addItem = () => {
        if (!newItem.creditor_name.trim()) return;
        setForm(f => ({
            ...f,
            negative_items: [...f.negative_items, { ...newItem, amount: parseFloat(newItem.amount || '0'), status: 'active' }]
        }));
        setNewItem({ bureau: 'Experian', item_type: 'collection', creditor_name: '', account_number: '', amount: '' });
    };

    const removeItem = (idx) => {
        setForm(f => ({ ...f, negative_items: f.negative_items.filter((_, i) => i !== idx) }));
    };

    const submit = async () => {
        setLoading(true);
        setError('');
        try {
            await API.post('/credit-repair/enroll', form);
            navigate('/credit-repair');
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const totalSteps = 4;
    const progressPercent = Math.round((step / totalSteps) * 100);

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="credit-repair-intake-page">
            <Navbar variant="light" />
            <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="text-center mb-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                        <Shield className="w-6 h-6 text-emerald-700" />
                    </div>
                    <h1 className="text-xl font-semibold text-zinc-900 font-['Outfit']">Credit Repair Enrollment</h1>
                    <p className="text-sm text-zinc-500 mt-1">Step {step} of {totalSteps}</p>
                </div>

                <Progress value={progressPercent} className="h-2 bg-zinc-200 mb-8" />

                {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>}

                <div className="bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8 shadow-sm">
                    {/* Step 1: Personal Info */}
                    {step === 1 && (
                        <div className="space-y-4 animate-fade-in-up" data-testid="intake-step-1">
                            <h2 className="text-lg font-medium text-zinc-900 font-['Outfit']">Personal Information</h2>
                            <div>
                                <Label className="text-sm text-zinc-700">Full Name</Label>
                                <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                                    className="border-zinc-200 mt-1" data-testid="cr-name-input" />
                            </div>
                            <div>
                                <Label className="text-sm text-zinc-700">Phone Number</Label>
                                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                    placeholder="(713) 555-0123" className="border-zinc-200 mt-1" data-testid="cr-phone-input" />
                            </div>
                            <div>
                                <Label className="text-sm text-zinc-700">Current Credit Score Range</Label>
                                <select value={form.current_score_range} onChange={e => setForm(f => ({ ...f, current_score_range: e.target.value }))}
                                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white mt-1" data-testid="cr-score-select">
                                    {SCORE_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-sm text-zinc-700">Target Score (for conventional loan)</Label>
                                <Input type="number" value={form.target_score} onChange={e => setForm(f => ({ ...f, target_score: parseInt(e.target.value) || 640 }))}
                                    className="border-zinc-200 mt-1" data-testid="cr-target-input" />
                            </div>
                            <div>
                                <Label className="text-sm text-zinc-700">Monthly Budget for Credit Repair</Label>
                                <Input type="number" value={form.monthly_budget || ''} onChange={e => setForm(f => ({ ...f, monthly_budget: parseFloat(e.target.value) || 0 }))}
                                    placeholder="e.g., 200" className="border-zinc-200 mt-1" data-testid="cr-budget-input" />
                            </div>
                        </div>
                    )}

                    {/* Step 2: Negative Items */}
                    {step === 2 && (
                        <div className="space-y-4 animate-fade-in-up" data-testid="intake-step-2">
                            <h2 className="text-lg font-medium text-zinc-900 font-['Outfit']">Known Negative Items</h2>
                            <p className="text-sm text-zinc-500">Add negative items currently on your credit report. These will be targets for disputes.</p>

                            {form.negative_items.length > 0 && (
                                <div className="space-y-2 mb-4">
                                    {form.negative_items.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 border border-zinc-200">
                                            <div>
                                                <span className="text-sm font-medium text-zinc-900">{item.creditor_name}</span>
                                                <span className="text-xs text-zinc-500 ml-2">{item.bureau} | {item.item_type.replace('_', ' ')} | ${item.amount.toLocaleString()}</span>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => removeItem(i)} className="text-red-400 h-6 w-6 p-0">
                                                <X className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-zinc-600">Bureau</Label>
                                        <select value={newItem.bureau} onChange={e => setNewItem(n => ({ ...n, bureau: e.target.value }))}
                                            className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="new-item-bureau">
                                            {BUREAUS.map(b => <option key={b} value={b}>{b}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-600">Type</Label>
                                        <select value={newItem.item_type} onChange={e => setNewItem(n => ({ ...n, item_type: e.target.value }))}
                                            className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="new-item-type">
                                            {ITEM_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-600">Creditor</Label>
                                        <Input value={newItem.creditor_name} onChange={e => setNewItem(n => ({ ...n, creditor_name: e.target.value }))}
                                            placeholder="e.g., Capital One" className="border-zinc-200 h-8 text-sm" data-testid="new-item-creditor" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-600">Amount</Label>
                                        <Input type="number" value={newItem.amount} onChange={e => setNewItem(n => ({ ...n, amount: e.target.value }))}
                                            placeholder="0" className="border-zinc-200 h-8 text-sm" data-testid="new-item-amount" />
                                    </div>
                                </div>
                                <Button size="sm" onClick={addItem} data-testid="add-item-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg">
                                    <Plus className="w-3 h-3 mr-1" /> Add Item
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Program Tier */}
                    {step === 3 && (
                        <div className="space-y-4 animate-fade-in-up" data-testid="intake-step-3">
                            <h2 className="text-lg font-medium text-zinc-900 font-['Outfit']">Choose Your Program</h2>
                            <div className="space-y-3">
                                {TIERS.map(t => (
                                    <label key={t.id} className={`block cursor-pointer rounded-xl border p-4 transition-all ${form.program_tier === t.id ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-100' : 'border-zinc-200 hover:border-zinc-300'}`}>
                                        <input type="radio" name="tier" value={t.id} checked={form.program_tier === t.id}
                                            onChange={() => setForm(f => ({ ...f, program_tier: t.id }))} className="hidden" />
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-sm font-semibold text-zinc-900">{t.name}</div>
                                                <div className="text-xs text-zinc-500 mt-0.5">{t.desc}</div>
                                            </div>
                                            <div className="text-lg font-bold text-emerald-700 font-['Outfit']">{t.price}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 4: Review */}
                    {step === 4 && (
                        <div className="space-y-4 animate-fade-in-up" data-testid="intake-step-4">
                            <h2 className="text-lg font-medium text-zinc-900 font-['Outfit']">Review & Enroll</h2>
                            <div className="space-y-3">
                                <InfoRow label="Name" value={form.full_name} />
                                <InfoRow label="Phone" value={form.phone} />
                                <InfoRow label="Current Score" value={form.current_score_range} />
                                <InfoRow label="Target Score" value={form.target_score} />
                                <InfoRow label="Budget" value={`$${form.monthly_budget}/mo`} />
                                <InfoRow label="Program" value={TIERS.find(t => t.id === form.program_tier)?.name || form.program_tier} />
                                <InfoRow label="Negative Items" value={`${form.negative_items.length} items`} />
                            </div>
                            {form.negative_items.length > 0 && (
                                <div className="bg-zinc-50 rounded-lg p-3 space-y-1">
                                    {form.negative_items.map((item, i) => (
                                        <div key={i} className="text-xs text-zinc-600">{item.bureau} | {item.creditor_name} | {item.item_type.replace('_', ' ')} | ${item.amount.toLocaleString()}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-200">
                        {step > 1 ? (
                            <Button variant="outline" onClick={() => setStep(s => s - 1)} className="border-zinc-200" data-testid="intake-back-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Back
                            </Button>
                        ) : (
                            <Button variant="outline" onClick={() => navigate('/credit-repair')} className="border-zinc-200">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
                            </Button>
                        )}
                        {step < totalSteps ? (
                            <Button onClick={() => setStep(s => s + 1)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6" data-testid="intake-next-btn">
                                Next <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6" data-testid="intake-submit-btn">
                                {loading ? 'Enrolling...' : <><CheckCircle className="w-4 h-4 mr-2" /> Enroll Now</>}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="flex justify-between items-center py-2 border-b border-zinc-100">
            <span className="text-sm text-zinc-500">{label}</span>
            <span className="text-sm font-medium text-zinc-900">{value}</span>
        </div>
    );
}
